import catalog from "../../../shared-client-attribution/harness-catalog.json" with { type: "json" };

export const HARNESS_CATALOG = catalog;
export const HARNESS_MAPPING_VERSION = catalog.version;

const own = (map, key) => Object.hasOwn(map, key) ? map[key] : undefined;

const CONDITION_KEYS = new Set(["field", "equals", "values", "present", "notIn", "single"]);
const RULE_KEYS = new Set(["when", "any", "field", "map", "extract", "ambiguous", "suppress", "single", "surface"]);
const VALUE_TYPES = { stringValue: "string", booleanValue: "boolean" };

// The SQL lowering in shared-analytics rejects the same shapes, so a rule that
// one reader would evaluate differently from the other cannot load at all.
export function validateHarnessCatalog(source) {
  const fail = message => { throw new Error(`harness catalog: ${message}`); };
  const reader = source.reader || fail("missing reader section");
  for (const key of [reader.defaultValueKey, ...Object.values(reader.typedValueKeys || {})]) {
    if (!Object.hasOwn(VALUE_TYPES, key)) fail(`unsupported value key ${key}`);
  }
  const extractSyntax = new RegExp(reader.extractSyntax);
  const isScalar = value => typeof value === "string" || typeof value === "boolean";
  const checkCondition = (condition, at) => {
    const keys = Object.keys(condition);
    if (typeof condition.field !== "string") fail(`${at}: condition needs a field`);
    if (keys.some(key => !CONDITION_KEYS.has(key))) fail(`${at}: unsupported condition key`);
    const operators = keys.filter(key => key !== "field" && key !== "single");
    if (operators.length !== 1) fail(`${at}: condition needs exactly one operator`);
    const [operator] = operators;
    // `single` already follows from notIn's "exactly one excluded value"
    // test, and a presence test has no value to count.
    if (Object.hasOwn(condition, "single") && (condition.single !== true || !["equals", "values"].includes(operator))) fail(`${at}: single applies only to equals or values`);
    if (operator === "present" && condition.present !== true) fail(`${at}: present must be true`);
    if (operator === "equals" && !isScalar(condition.equals)) fail(`${at}: equals needs a scalar`);
    if ((operator === "values" || operator === "notIn") && !(Array.isArray(condition[operator]) && condition[operator].length && condition[operator].every(isScalar))) fail(`${at}: ${operator} needs scalars`);
  };
  source.rules.forEach((rule, index) => {
    const at = `rule ${index}`;
    if (Object.keys(rule).some(key => !RULE_KEYS.has(key))) fail(`${at}: unsupported rule key`);
    if (Boolean(rule.surface) === Boolean(rule.map)) fail(`${at}: needs exactly one of surface or map`);
    if (rule.map && (typeof rule.field !== "string" || !Object.hasOwn(source.maps, rule.map))) fail(`${at}: map needs a field and a known map`);
    if (rule.surface && ["field", "extract", "ambiguous", "single"].some(key => Object.hasOwn(rule, key))) fail(`${at}: surface rules take no field qualifiers`);
    if (rule.extract !== undefined && !extractSyntax.test(rule.extract)) fail(`${at}: extract is outside the shared regex subset`);
    if (rule.any !== undefined && !(Array.isArray(rule.any) && rule.any.length)) fail(`${at}: any needs conditions`);
    [...(rule.when || []), ...(rule.any || [])].forEach(condition => checkCondition(condition, at));
  });
  return source;
}
validateHarnessCatalog(catalog);

// The same declarative rules are lowered to SQL by shared-analytics.
export function harnessCandidates(records, rules = catalog.rules) {
  const values = field => [...new Set((records.get(field) || []).map(record => record.value))];
  const test = condition => {
    const observed = values(condition.field);
    if (condition.present) return observed.some(value => typeof value === "string");
    if (condition.notIn) return observed.length !== 1 || !condition.notIn.includes(observed[0]);
    if (condition.single && observed.length !== 1) return false;
    return observed.some(value => condition.values ? condition.values.includes(value) : value === condition.equals);
  };
  let candidates = [];
  let unresolved = false;
  for (const rule of rules) {
    if (!((rule.when || []).every(test)) || (rule.any && !rule.any.some(test))) continue;
    const support = [...(rule.when || []), ...(rule.any || [])].flatMap(condition => (records.get(condition.field) || []).map(record => record.fact));
    let matches;
    if (rule.surface) matches = [{ surfaceId: rule.surface, facts: support }];
    else {
      const observed = records.get(rule.field) || [];
      const distinct = values(rule.field);
      if (rule.single && distinct.length !== 1) continue;
      const mapping = catalog.maps[rule.map];
      const key = value => rule.extract && typeof value === "string" ? new RegExp(rule.extract).exec(value)?.[1] : value;
      if (rule.ambiguous && distinct.length > 1 && distinct.some(value => !own(mapping, key(value)))) unresolved = true;
      matches = observed.flatMap(record => {
        const surfaceId = own(mapping, key(record.value));
        return surfaceId ? [{ surfaceId, facts: [...support, record.fact] }] : [];
      });
    }
    if (matches.length && rule.suppress) candidates = candidates.filter(candidate => !rule.suppress.includes(candidate.surfaceId));
    candidates.push(...matches);
  }
  return { candidates, unresolved };
}

export function harnessCategory(productId, host, conflict = false) {
  if (conflict) return "harness:conflicting";
  if (!productId) return "harness:unknown";
  const category = Object.entries(catalog.categories).find(([, category]) => category.productId === productId && category.host === (host || null))?.[0] || "unknown";
  return `harness:${category}`;
}

// ---- Session harness reads -------------------------------------------------

const statusTag = status => {
  if (!Object.hasOwn(catalog.statuses, status)) throw new Error(`harness catalog has no "${status}" status`);
  return `harness:${status}`;
};
export const HARNESS_UNKNOWN = statusTag("unknown");
export const HARNESS_MULTIPLE = statusTag("multiple");
export const HARNESS_UNKNOWN_LABEL = catalog.statuses.unknown;

// A session's harness as two metrics over a per-request harness field: its
// value, and how many distinct values it has, so a session that ran on more
// than one reads as multiple rather than whichever value MAX picks.
export function harnessSessionMetrics(field, alias = "harness") {
  return [
    { outputAlias: alias, computed: { field, op: "AGGREGATE_OP_MAX" } },
    { outputAlias: `${alias}_count`, computed: { field, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
  ];
}

// The harness a row of harnessSessionMetrics names. Null only when the row
// has no harness column at all: a server that predates the field had it
// dropped from the query, so nothing was read and the caller may fall back to
// its own evidence; a read that found no value is the catalog's unknown.
export function readSessionHarness(row, alias = "harness") {
  if (!row || !Object.hasOwn(row, alias)) return null;
  if (Number(row[`${alias}_count`]) > 1) return HARNESS_MULTIPLE;
  return row[alias] ? String(row[alias]) : HARNESS_UNKNOWN;
}
